import React from 'react';

import autoBind from 'react-autobind';

// import other components within the same project here

import Localization from "../Localization.jsx";
import AuthService from "../tools/AuthService.js";

import {
  Modal,
  Box,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Alert,
  Divider,
  Loader,
  Tooltip,
} from '@mantine/core';
import {
  IconUserCircle,
  IconLock,
  IconAlertCircle,
  IconInfoCircle,
} from '@tabler/icons-react';

// ---------------------------------------------------------------------------
// The choice dialog for an EdCity sign-in whose identity is UNLINKED.
//
// The callback used to end such a sign-in with a terminal refusal
// (?edconnect=0&edconnect_error=not_linked). It now parks the identity in a
// single-use server-side pending ticket and redirects back with
// ?edconnect=2&pend_code=... — RootPage strips the params and opens this
// dialog, which PEEKS at the ticket (POST /api/sso/edcity_pending_info, no
// redeem) to render who EdCity says just signed in, then offers:
//
//   - school known:   a CHOICE screen carrying nothing but the two offers,
//                     each with an (i) that reveals, on hover or focus, a
//                     one-line tip naming which user it is for:
//                     [Create new account] first — ONLY when EdData proved the
//                     identity is staff — behind a confirmation that says
//                     plainly the user has no EduVenture-AI account yet, then
//                     [Log in & link], which opens the username/password
//                     fields on a second screen (the link consumes the ticket).
//   - school unknown: a refusal (the school is not onboarded); no offer.
//
// The credential fields deliberately do NOT render until [Log in & link] is
// picked: a first-time EdCity user who has no account at all was previously
// met by a password form they cannot fill in, which reads as "you are supposed
// to know a password" rather than as one of two equal choices.
//
// The ticket stays unconsumed until the user picks, so a slow read of this
// dialog costs nothing — unlike the ev_code, which is spent on mount.
// ---------------------------------------------------------------------------

// Dialog phases. `choose` is the interactive screen; everything else is a
// message with a single button.
const PHASE_LOADING = 'loading';        // peeking at the pending ticket
const PHASE_CHOOSE = 'choose';          // the two offers and their (i)s, nothing else
const PHASE_LINK_FORM = 'linkForm';     // username/password, after picking "log in & link"
const PHASE_REFUSED = 'refused';        // school not onboarded
const PHASE_EXPIRED = 'expired';        // ticket gone — retry EdCity login
const PHASE_ERROR = 'error';            // peek failed for any other reason
const PHASE_LINK_FAILED = 'linkFailed'; // password login OK, link refused

// One choice = a button that fills the row with its (i) parked at the right-hand
// end. `nowrap` matters: the tip must never drop onto its own line and turn one
// offer into what looks like two.
const TIP_ROW = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' };

// The (i)'s own width. The icon is a flex item, so every button in a TIP_ROW is
// this much (plus the row gap) narrower than the modal body — which is why the
// tip-less Cancel gets a matching empty gutter instead of `fullWidth`. Shared so
// the icon and the gutter can never drift apart.
const TIP_ICON_SIZE = 20;

class EdCityUnlinkedDialog extends React.Component {
  constructor(props) {
    super(props);

    // state variables here
    this.state = {
      phase: PHASE_LOADING,
      // The ticket peek: {display_name, school_known, school_name, is_staff}.
      info: null,
      username: "",
      password: "",
      // A request is in flight (login+link or provision) — the buttons lock so
      // a second click cannot race the first one on a single-use ticket.
      busy: false,
      // Why the link was refused, shown in PHASE_LINK_FAILED.
      linkErrorMessage: '',
      // The successful password login held back while the link error is shown;
      // the user continues into this session after acknowledging.
      pendingLoginRes: null,
    };
    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    // A new pending ticket arrived (RootPage saw ?edconnect=2): reset and peek.
    if (this.props.pending && !prevProps.pending) {
      this.setState({
        phase: PHASE_LOADING,
        info: null,
        username: "",
        password: "",
        busy: false,
        linkErrorMessage: '',
        pendingLoginRes: null,
      });
      this.fetchPendingInfo();
    }
  }

  fetchPendingInfo() {
    AuthService.getEdCityPendingInfo(this.props.pending.pendCode)
      .then((result) => {
        if (!this.props.pending) {
          return; // dialog closed while the request was in flight
        }
        // OK-path guard (API.jsx convention): a 2xx body can still be
        // null/non-object, so never read fields before checking the shape.
        const body = (result && result.body && typeof result.body === 'object')
          ? result.body : null;
        if (result && result.ok && body) {
          // The server's school_known is authoritative over the URL hint — the
          // query param is only there so the redirect is self-describing.
          this.setState({
            info: body,
            phase: body.school_known ? PHASE_CHOOSE : PHASE_REFUSED,
          });
        } else if (result && result.status === 410) {
          this.setState({ phase: PHASE_EXPIRED });
        } else {
          this.setState({ phase: PHASE_ERROR });
        }
      })
      .catch(() => {
        if (this.props.pending) {
          this.setState({ phase: PHASE_ERROR });
        }
      });
  }

  usernameChanged(e) {
    this.setState({ username: e.target.value });
  }

  passwordChanged(e) {
    this.setState({ password: e.target.value });
  }

  handleEnterPressed(e) {
    if (e.key === "Enter") {
      this.handleLoginAndLink();
    }
  }

  handleLoginAndLink() {
    if (this.state.busy) {
      return;
    }
    this.setState({ busy: true });
    // The same call the ordinary login form makes (LoginCreateSession also
    // just delegates to AuthService.login) — success stores the JWT pair, so
    // the link call right after carries the fresh bearer.
    AuthService.login(this.state.username, this.state.password)
      .then((res) => {
        this.finishLink(res);
      })
      .catch(() => {
        // Same copy as LoginPage.loginFailed for the ordinary login form —
        // localized here, because everything else on this screen is.
        this.setState({ busy: false });
        this.props.root.addNotification(Localization.strings.LOGIN_INCORRECT, false);
      });
  }

  finishLink(res) {
    AuthService.linkEdCityPending(this.props.pending.pendCode)
      .then((result) => {
        const body = (result && result.body && typeof result.body === 'object')
          ? result.body : {};
        if (result && result.ok) {
          this.props.root.addNotification(Localization.strings.EDCONNECT_BIND_SUCCESS, true);
          // The link may have just ADOPTED a school onto the account
          // (adopt_school_if_unset), so the pre-link login response can be
          // stale — re-read the session and adopt the post-adoption
          // user/school, falling back to the pre-link response if the
          // re-read fails.
          AuthService.fetchSession()
            .then((session) => {
              this.adoptLogin(session ? AuthService.makeResponse(200, session) : res);
            })
            .catch(() => {
              this.adoptLogin(res);
            });
          return;
        }
        // The password login itself SUCCEEDED — only the link was refused.
        // Show why, then let the user continue into their account: refusing
        // entry after a correct password would punish them for the link's
        // failure, and the link can always be retried from the account menu.
        let message = Localization.strings.EDCONNECT_BIND_FAILED;
        if (result && result.status === 403 && body.error === 'school_mismatch') {
          message = Localization.strings.EDCONNECT_BIND_SCHOOL_MISMATCH;
        } else if (result && result.status === 409 && body.error === 'identity_conflict') {
          message = body.created_account
            ? Localization.strings.EDCITY_CREATED_ACCOUNT_NO_MIGRATE
            : Localization.strings.EDCONNECT_BIND_ALREADY_OTHER;
        } else if (result && result.status === 410) {
          message = Localization.strings.EDCITY_PENDING_EXPIRED;
        }
        this.setState({
          busy: false,
          phase: PHASE_LINK_FAILED,
          linkErrorMessage: message,
          pendingLoginRes: res,
        });
      })
      .catch(() => {
        this.setState({
          busy: false,
          phase: PHASE_LINK_FAILED,
          linkErrorMessage: Localization.strings.EDCONNECT_BIND_FAILED,
          pendingLoginRes: res,
        });
      });
  }

  /**
   * Hand the session over exactly as a successful password login would: via
   * LoginPage.loginOK, which runs the T&C agreement gate before RootPage
   * adopts the session. The dialog closes FIRST so the agreement dialog
   * (which lives inside LoginPage) is not buried underneath it. The status is
   * still STATUS_REQUIRE_LOGIN while this dialog shows, so LoginPage is
   * guaranteed mounted; the root fallback only covers a torn-down ref.
   */
  adoptLogin(res) {
    this.handleClose();
    const loginPage = this.props.root.refs ? this.props.root.refs.loginPage : null;
    if (loginPage && typeof loginPage.loginOK === 'function') {
      loginPage.loginOK(res, false);
    } else {
      this.props.root.loginOK(res, false);
    }
  }

  handleContinueAfterLinkFail() {
    if (this.state.pendingLoginRes) {
      this.adoptLogin(this.state.pendingLoginRes);
    } else {
      this.handleClose();
    }
  }

  /**
   * "Log in & link" on the choice screen: reveal the credential fields.
   *
   * Nothing is sent and the ticket is untouched — this is still a local screen
   * change, so BACK returns to the choice with the ticket as good as new.
   */
  showLinkForm() {
    if (this.state.busy) {
      return;
    }
    this.setState({ phase: PHASE_LINK_FORM });
  }

  /** BACK from the credential fields — drop what was typed, re-offer both. */
  backToChoose() {
    if (this.state.busy) {
      return;
    }
    this.setState({ phase: PHASE_CHOOSE, username: "", password: "" });
  }

  handleCreateClicked() {
    // Explicit confirmation before provisioning — the spec's whole point is
    // that creating replaces the old SILENT auto-provision, never re-adds it.
    // The copy states only what is about to happen, and repeats the identity so
    // the account being created is never a surprise.
    const info = this.state.info || {};
    const identity = [info.display_name, info.school_name].filter(Boolean).join('\n');
    const content = identity
      ? Localization.strings.EDCITY_CREATE_CONFIRM + '\n\n' + identity
      : Localization.strings.EDCITY_CREATE_CONFIRM;
    if (this.props.root && this.props.root.refs && this.props.root.refs.confirmDialog) {
      this.props.root.refs.confirmDialog.show(
        {
          title: Localization.strings.EDCITY_CREATE_ACCOUNT,
          content: content,
          confirm: Localization.strings.CONFIRM,
          cancel: Localization.strings.CANCEL,
        },
        () => {
          this.provisionAccount();
        },
        () => {}
      );
    }
  }

  provisionAccount() {
    if (this.state.busy) {
      return;
    }
    this.setState({ busy: true });
    AuthService.provisionFromEdCity(this.props.pending.pendCode)
      .then((res) => {
        // Same adoption path as an ordinary EdCity login: the ticket has been
        // consumed and the response is byte-shaped like the ev_code exchange.
        this.handleClose();
        this.props.root.adoptSsoSession(res);
      })
      .catch((e) => {
        if (!this.props.pending) {
          return;
        }
        if (e && e.status === 410) {
          this.setState({ busy: false, phase: PHASE_EXPIRED });
        } else {
          // Covers 403 not_eligible too — unreachable through this UI (the
          // button only renders for EdData-proven staff), so it needs no copy
          // of its own.
          this.setState({ busy: false });
          this.props.root.addNotification(Localization.strings.THIRD_APRTY_LOGIN_FAILED, false);
        }
      });
  }

  handleCancel() {
    if (this.state.busy) {
      return;
    }
    // Esc / the X button in PHASE_LINK_FAILED must behave like OK: the
    // password login already SUCCEEDED, so dismissing continues into the held
    // session instead of stranding the freshly minted token pair.
    if (this.state.phase === PHASE_LINK_FAILED) {
      this.handleContinueAfterLinkFail();
      return;
    }
    this.handleClose();
  }

  handleClose() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  renderIdentity() {
    const info = this.state.info || {};
    return (
      <Box>
        {/* Who EdCity says just signed in — from the ticket peek, so the user
            can tell this dialog is about the identity they picked at CAS. */}
        <Text fw={600}>{info.display_name || ''}</Text>
        {info.school_name ? (
          <Text size="sm" c="dimmed">{info.school_name}</Text>
        ) : null}
        <Divider my="md" />
      </Box>
    );
  }

  renderMessagePhase(message, buttonLabel, onButton) {
    return (
      <Box>
        <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
          {message}
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button onClick={onButton}>{buttonLabel}</Button>
        </Group>
      </Box>
    );
  }

  /**
   * The (i) beside a choice button. The tip is on demand now rather than
   * printed under every button, which keeps the two offers side by side and
   * the modal short.
   *
   * Same Tooltip idiom as views/LabelWithInfo.jsx — hover plus focus and
   * touch, so the tip is still reachable from a keyboard or an iPad, where
   * hover does not exist. It sits OUTSIDE the button on purpose: inside, a
   * click aimed at the icon would fire the choice, and one of these choices
   * creates an account.
   */
  renderTipIcon(info) {
    return (
      <Tooltip
        label={info}
        withArrow
        multiline
        w={260}
        // Above the icon, right-aligned to it. NOT "left": the icon sits at
        // the end of its own button, so a tooltip opening leftward lands on
        // top of the very button it is explaining.
        position="top-end"
        events={{ hover: true, focus: true, touch: true }}
      >
        {/* The span is load-bearing. Tooltip clones its child to attach the
            hover handlers and the reference ref; handing it a tabler <svg>
            directly attaches props that never fire, so the tip stays shut.
            A plain host element takes them reliably — and it is where the
            tabIndex belongs anyway, so Tab reaches the tip. */}
        <span
          role="img"
          aria-label={info}
          tabIndex={0}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            color: 'var(--mantine-color-gray-6)',
            cursor: 'help',
            flex: 'none',
            outlineOffset: 2,
          }}
        >
          <IconInfoCircle size={TIP_ICON_SIZE} stroke={1.8} />
        </span>
      </Tooltip>
    );
  }

  /**
   * The choice screen: the two offers and nothing else.
   *
   * With the credential fields gone the button labels alone no longer say what
   * either choice does, and the whole point of the screen is that the user
   * picks knowingly — so each button gets an (i) whose tip names the user it is
   * for ("you already have an account" / "you do not have one yet"). The tips
   * are on demand rather than printed inline, which keeps the two offers
   * adjacent and the modal short.
   */
  renderChoose() {
    const info = this.state.info || {};
    // Staff per the server's peek (EdData-proven), not per the URL hint — a
    // hand-edited &staff=1 must not conjure the create button. The server
    // refuses non-staff provisioning anyway; this just keeps the UI honest.
    const isStaff = Boolean(this.props.pending && this.props.pending.staff)
      && Boolean(info.is_staff);

    return (
      <Box>
        {this.renderIdentity()}

        <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
          {Localization.strings.EDCITY_CHOOSE_PROMPT}
        </Text>

        {isStaff && (
          <Box mt="md" style={TIP_ROW}>
            <Button
              style={{ flex: 1 }}
              variant="outline"
              onClick={this.handleCreateClicked}
              // `loading` (not just `disabled`) — provisionAccount keeps this
              // screen on show for the whole round trip, and Mantine draws the
              // spinner only from `loading`. Without it the dialog just greys
              // out and looks hung; it disables the button either way.
              loading={this.state.busy}
            >
              {Localization.strings.EDCITY_CREATE_ACCOUNT}
            </Button>
            {this.renderTipIcon(Localization.strings.EDCITY_CREATE_ACCOUNT_HINT)}
          </Box>
        )}

        <Box mt="md" style={TIP_ROW}>
          <Button
            style={{ flex: 1 }}
            onClick={this.showLinkForm}
            disabled={this.state.busy}
          >
            {Localization.strings.EDCITY_LOGIN_AND_LINK}
          </Button>
          {this.renderTipIcon(Localization.strings.EDCITY_LOGIN_AND_LINK_HINT)}
        </Box>

        {/* Cancel has no tip, but it still needs the same right edge as the two
            offers above it. `fullWidth` here would run it 28px (icon + gap) past
            them, putting its label off-centre against theirs and sliding its
            hover background under the (i)s — so it takes the same row shape with
            an empty gutter where the icon would be. */}
        <Box mt="lg" style={TIP_ROW}>
          <Button
            style={{ flex: 1 }}
            variant="subtle"
            onClick={this.handleCancel}
            disabled={this.state.busy}
          >
            {Localization.strings.CANCEL}
          </Button>
          <span aria-hidden="true" style={{ width: TIP_ICON_SIZE, flex: 'none' }} />
        </Box>
      </Box>
    );
  }

  /**
   * Second screen of the "log in & link" branch: the credential fields, which
   * only ever render once the user has asked for them.
   */
  renderLinkForm() {
    return (
      <Box>
        {this.renderIdentity()}

        <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
          {Localization.strings.EDCITY_LINK_REMINDER}
        </Text>

        <TextInput
          mt="md"
          size="md"
          label={Localization.strings.USERNAME + "/" + Localization.strings.EMAIL}
          id="edcity-link-username"
          name="username"
          // The user just asked for this field; carry the caret into it rather
          // than making them click the box they only now made appear.
          autoFocus
          value={this.state.username}
          onChange={this.usernameChanged}
          onKeyDown={this.handleEnterPressed}
          leftSection={<IconUserCircle size={20} />}
        />

        <PasswordInput
          mt="sm"
          size="md"
          label={Localization.strings.PASSWORD}
          id="edcity-link-password"
          name="password"
          value={this.state.password}
          onChange={this.passwordChanged}
          onKeyDown={this.handleEnterPressed}
          leftSection={<IconLock size={20} />}
        />

        <Button mt="md" fullWidth onClick={this.handleLoginAndLink} loading={this.state.busy}>
          {Localization.strings.EDCITY_LOGIN_AND_LINK}
        </Button>

        <Button
          mt="sm"
          fullWidth
          variant="subtle"
          onClick={this.backToChoose}
          disabled={this.state.busy}
        >
          {Localization.strings.BACK}
        </Button>
      </Box>
    );
  }

  renderLinkFailed() {
    return (
      <Box>
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={18} />}
        >
          <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
            {this.state.linkErrorMessage}
          </Text>
        </Alert>
        {/* OK = continue into the signed-in account without the link. */}
        <Group justify="flex-end" mt="lg">
          <Button onClick={this.handleContinueAfterLinkFail}>
            {Localization.strings.OK}
          </Button>
        </Group>
      </Box>
    );
  }

  render() {
    const opened = Boolean(this.props.pending);
    const { phase } = this.state;

    return (
      <Modal
        opened={opened}
        onClose={this.handleCancel}
        title={<Text fw={600}>{Localization.strings.EDCITY_UNLINKED_TITLE}</Text>}
        size="md"
        radius="md"
        centered
        // A misclick on the overlay would throw away the whole EdCity round
        // trip — closing is an explicit choice here (the X, Cancel or Back).
        closeOnClickOutside={false}
        // Escape is the same accident, and worse: every mounted Mantine Modal
        // installs its OWN window-capture keydown listener, so while the
        // "create account?" confirmation is up, one Escape fires both — the
        // confirm closes AND this dialog closes under it. RootPage has already
        // stripped ?pend_code from the URL by then, so there is no way back to
        // this screen short of redoing the whole EdCity CAS round trip.
        closeOnEscape={false}
      >
        {phase === PHASE_LOADING && (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <Loader />
          </Box>
        )}

        {phase === PHASE_CHOOSE && this.renderChoose()}

        {phase === PHASE_LINK_FORM && this.renderLinkForm()}

        {phase === PHASE_REFUSED && this.renderMessagePhase(
          Localization.strings.EDCITY_SCHOOL_UNKNOWN,
          Localization.strings.CLOSE,
          this.handleCancel,
        )}

        {phase === PHASE_EXPIRED && this.renderMessagePhase(
          Localization.strings.EDCITY_PENDING_EXPIRED,
          Localization.strings.CLOSE,
          this.handleCancel,
        )}

        {phase === PHASE_ERROR && this.renderMessagePhase(
          Localization.strings.THIRD_APRTY_LOGIN_FAILED,
          Localization.strings.CLOSE,
          this.handleCancel,
        )}

        {phase === PHASE_LINK_FAILED && this.renderLinkFailed()}
      </Modal>
    );
  }
}

export default EdCityUnlinkedDialog;
